import { adminFetch } from "./_client";

// Admin client for the Trust & Safety user-reports queue (Task #7540).
// End users file reports (in-app report + block, App Store Guideline 1.2);
// admins triage them here: open the reported content, then resolve/dismiss.

export type UserReportStatus = "open" | "reviewing" | "resolved" | "dismissed";
export type UserReportTargetType = "message" | "conversation" | "user" | "listing" | "review";

export interface UserReportPerson {
  id: number;
  name: string;
  email: string | null;
}

export interface UserReportItem {
  id: number;
  status: UserReportStatus;
  createdAt: string;
  reason: string;
  details: string | null;
  reviewedAt: string | null;
  reviewedBy: UserReportPerson | null;
  reporter: UserReportPerson;
  target: {
    type: UserReportTargetType;
    id: string;
    /** Server-hydrated label (reported user's name, listing car label). */
    label: string | null;
    /** For message/conversation targets: the conversation to open in /admin/messages?conv=… */
    conversationId: string | null;
  };
}

export interface UserReportsResponse {
  items: UserReportItem[];
  page: number;
  pageSize: number;
  total: number;
}

export function fetchUserReports(
  token: string,
  params: { status?: UserReportStatus; type?: UserReportTargetType; page?: number; pageSize?: number } = {},
) {
  const sp = new URLSearchParams();
  if (params.status) sp.set("status", params.status);
  if (params.type) sp.set("type", params.type);
  if (params.page) sp.set("page", String(params.page));
  if (params.pageSize) sp.set("pageSize", String(params.pageSize));
  const qs = sp.toString();
  return adminFetch<UserReportsResponse>(`/admin/user-reports${qs ? `?${qs}` : ""}`, token);
}

// Task #7549 — claim an open report (open → reviewing) so two admins don't
// work the same case. A second claim gets a 409 naming who already has it.
export function startReviewUserReport(token: string, id: number) {
  return adminFetch<{ ok: true; status: "reviewing"; claimedAt: string }>(`/admin/user-reports/${id}/start-review`, token, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

// Task #7552 — release a claimed report back to the open queue
// (reviewing → open). Only the claimer or a senior admin (users.moderate)
// may release; others get a 403.
export function releaseUserReport(token: string, id: number) {
  return adminFetch<{ ok: true; status: "open"; alreadyOpen?: boolean }>(`/admin/user-reports/${id}/release`, token, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

export function resolveUserReport(token: string, id: number) {
  return adminFetch<{ ok: true; status: "resolved"; reviewedAt: string }>(`/admin/user-reports/${id}/resolve`, token, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}

export function dismissUserReport(token: string, id: number) {
  return adminFetch<{ ok: true; status: "dismissed"; reviewedAt: string }>(`/admin/user-reports/${id}/dismiss`, token, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
}
